import { useCallback } from 'react';
import type { InfiniteData, QueryKey } from 'react-query';
import { useQueryClient } from 'react-query';

import type { SearchResultsBase } from '../../types/search';

export function useInfiniteQueryResults<
  TData extends Partial<BasicEntity>,
  TResults extends SearchResultsBase<TData> = SearchResultsBase<TData>
>(queryKey: QueryKey) {
  const queryClient = useQueryClient();

  const removeData = useCallback(
    (id: string, idPropertyName: keyof TData = 'id') => {
      queryClient.setQueryData<InfiniteData<TResults> | undefined>(
        queryKey,
        (prevResults) => {
          if (!prevResults) {
            return undefined;
          }

          // Remove existing
          return {
            ...prevResults,
            pages: prevResults.pages.map((page) => ({
              ...page,
              results: page.results.filter(
                (result) => result[idPropertyName] !== id
              ),
              total: page.total - 1,
            })),
          };
        }
      );
    },
    [queryClient, queryKey]
  );

  const unshiftData = useCallback(
    (results: TData[], idPropertyName: keyof TData = 'id') => {
      queryClient.setQueryData<InfiniteData<TResults>>(
        queryKey,
        (prevResults) => {
          if (!prevResults) {
            return {
              pageParams: [],
              pages: [
                {
                  results,
                  total: 1,
                } as TResults,
              ],
            };
          }

          const resultIds = results.map((result) => result[idPropertyName]);
          return {
            ...prevResults,
            pages: prevResults.pages.map((page) => {
              return {
                ...page,
                results: [
                  ...results,
                  ...page.results.filter(
                    (result) => !resultIds.includes(result[idPropertyName])
                  ),
                ],
                total: page.total + results.length,
              };
            }),
          };
        }
      );
    },
    [queryClient, queryKey]
  );

  const updateData = useCallback(
    (data: TData, idPropertyName: keyof TData = 'id') => {
      queryClient.setQueryData<InfiniteData<TResults> | undefined>(
        queryKey,
        (prevResults) => {
          if (!prevResults) {
            return undefined;
          }

          // Update existing
          return {
            ...prevResults,
            pages: prevResults.pages.map((page) => ({
              ...page,
              results: page.results.map((result) =>
                result[idPropertyName] === data[idPropertyName] ? data : result
              ),
            })),
          };
        }
      );
    },
    [queryClient, queryKey]
  );

  return {
    removeData,
    unshiftData,
    updateData,
  };
}
